import * as THREE from 'three';
import { PointCloudTree } from './point-cloud-tree.js';
import { PointSizeType, ClipTask, ElevationGradientRepeat } from '../shared/defines.js';
import { debug } from '../services/config.service';

// Copied from three.js: WebGLRenderer.js — updated for WebGL2
function paramThreeToGL(_gl: WebGL2RenderingContext, p: any): number {
  if (p === THREE.RepeatWrapping) return _gl.REPEAT;
  if (p === THREE.ClampToEdgeWrapping) return _gl.CLAMP_TO_EDGE;
  if (p === THREE.MirroredRepeatWrapping) return _gl.MIRRORED_REPEAT;

  if (p === THREE.NearestFilter) return _gl.NEAREST;
  if (p === THREE.NearestMipmapNearestFilter) return _gl.NEAREST_MIPMAP_NEAREST;
  if (p === THREE.NearestMipmapLinearFilter) return _gl.NEAREST_MIPMAP_LINEAR;

  if (p === THREE.LinearFilter) return _gl.LINEAR;
  if (p === THREE.LinearMipmapNearestFilter) return _gl.LINEAR_MIPMAP_NEAREST;
  if (p === THREE.LinearMipmapLinearFilter) return _gl.LINEAR_MIPMAP_LINEAR;

  if (p === THREE.UnsignedByteType) return _gl.UNSIGNED_BYTE;
  if (p === THREE.UnsignedShort4444Type) return _gl.UNSIGNED_SHORT_4_4_4_4;
  if (p === THREE.UnsignedShort5551Type) return _gl.UNSIGNED_SHORT_5_5_5_1;

  if (p === THREE.ByteType) return _gl.BYTE;
  if (p === THREE.ShortType) return _gl.SHORT;
  if (p === THREE.UnsignedShortType) return _gl.UNSIGNED_SHORT;
  if (p === THREE.IntType) return _gl.INT;
  if (p === THREE.UnsignedIntType) return _gl.UNSIGNED_INT;
  if (p === THREE.FloatType) return _gl.FLOAT;

  // WebGL2: half float is built-in
  if (p === THREE.HalfFloatType) return _gl.HALF_FLOAT;

  if (p === THREE.AlphaFormat) return _gl.ALPHA;
  if (p === THREE.RGBAFormat) return _gl.RGBA;
  if (p === THREE.DepthFormat) return _gl.DEPTH_COMPONENT;
  if (p === THREE.DepthStencilFormat) return _gl.DEPTH_STENCIL;

  if (p === THREE.AddEquation) return _gl.FUNC_ADD;
  if (p === THREE.SubtractEquation) return _gl.FUNC_SUBTRACT;
  if (p === THREE.ReverseSubtractEquation) return _gl.FUNC_REVERSE_SUBTRACT;

  if (p === THREE.ZeroFactor) return _gl.ZERO;
  if (p === THREE.OneFactor) return _gl.ONE;
  if (p === THREE.SrcColorFactor) return _gl.SRC_COLOR;
  if (p === THREE.OneMinusSrcColorFactor) return _gl.ONE_MINUS_SRC_COLOR;
  if (p === THREE.SrcAlphaFactor) return _gl.SRC_ALPHA;
  if (p === THREE.OneMinusSrcAlphaFactor) return _gl.ONE_MINUS_SRC_ALPHA;
  if (p === THREE.DstAlphaFactor) return _gl.DST_ALPHA;
  if (p === THREE.OneMinusDstAlphaFactor) return _gl.ONE_MINUS_DST_ALPHA;

  if (p === THREE.DstColorFactor) return _gl.DST_COLOR;
  if (p === THREE.OneMinusDstColorFactor) return _gl.ONE_MINUS_DST_COLOR;
  if (p === THREE.SrcAlphaSaturateFactor) return _gl.SRC_ALPHA_SATURATE;

  if (
    p === THREE.RGB_S3TC_DXT1_Format ||
    p === THREE.RGBA_S3TC_DXT1_Format ||
    p === THREE.RGBA_S3TC_DXT3_Format ||
    p === THREE.RGBA_S3TC_DXT5_Format
  ) {
    const extension = _gl.getExtension('WEBGL_compressed_texture_s3tc');

    if (extension !== null) {
      if (p === THREE.RGB_S3TC_DXT1_Format) return extension.COMPRESSED_RGB_S3TC_DXT1_EXT;
      if (p === THREE.RGBA_S3TC_DXT1_Format) return extension.COMPRESSED_RGBA_S3TC_DXT1_EXT;
      if (p === THREE.RGBA_S3TC_DXT3_Format) return extension.COMPRESSED_RGBA_S3TC_DXT3_EXT;
      if (p === THREE.RGBA_S3TC_DXT5_Format) return extension.COMPRESSED_RGBA_S3TC_DXT5_EXT;
    }
  }

  // WebGL2: Min/Max equations are built-in
  if (p === THREE.MinEquation) return _gl.MIN;
  if (p === THREE.MaxEquation) return _gl.MAX;

  // WebGL2: depth textures are built-in
  if (p === THREE.UnsignedInt248Type) return _gl.UNSIGNED_INT_24_8;

  return 0;
}

let attributeLocations: Record<string, { name: string; location: number }> = {
  position: { name: 'position', location: 0 },
  color: { name: 'color', location: 1 },
  rgba: { name: 'color', location: 1 },
  intensity: { name: 'intensity', location: 2 },
  classification: { name: 'classification', location: 3 },
  returnNumber: { name: 'returnNumber', location: 4 },
  'return number': { name: 'returnNumber', location: 4 },
  returns: { name: 'returnNumber', location: 4 },
  numberOfReturns: { name: 'numberOfReturns', location: 5 },
  'number of returns': { name: 'numberOfReturns', location: 5 },
  pointSourceID: { name: 'pointSourceID', location: 6 },
  'source id': { name: 'pointSourceID', location: 6 },
  'point source id': { name: 'pointSourceID', location: 6 },
  indices: { name: 'indices', location: 7 },
  normal: { name: 'normal', location: 8 },
  spacing: { name: 'spacing', location: 9 },
  'gps-time': { name: 'gpsTime', location: 10 },
  aExtra: { name: 'aExtra', location: 11 },
};

class Shader {
  gl: WebGL2RenderingContext;
  name: string;
  vsSource: string;
  fsSource: string;
  cache: Map<string, any>;
  vs: WebGLShader | null;
  fs: WebGLShader | null;
  program: WebGLProgram | null;
  uniformLocations: Record<string, WebGLUniformLocation | null>;
  attributeLocations: Record<string, number>;
  uniformBlockIndices: Record<string, number>;
  uniformBlocks: Record<string, any>;
  uniforms: Record<string, any>;

  constructor(gl: WebGL2RenderingContext, name: string, vsSource: string, fsSource: string) {
    this.gl = gl;
    this.name = name;
    this.vsSource = vsSource;
    this.fsSource = fsSource;

    this.cache = new Map();

    this.vs = null;
    this.fs = null;
    this.program = null;

    this.uniformLocations = {};
    this.attributeLocations = {};
    this.uniformBlockIndices = {};
    this.uniformBlocks = {};
    this.uniforms = {};

    this.update(vsSource, fsSource);
  }

  update(vsSource: string, fsSource: string): void {
    this.vsSource = vsSource;
    this.fsSource = fsSource;

    this.linkProgram();
  }

  compileShader(shader: WebGLShader, source: string): void {
    let gl = this.gl;

    gl.shaderSource(shader, source);

    gl.compileShader(shader);

    let success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
    if (!success) {
      let info = gl.getShaderInfoLog(shader);
      let numberedSource = source
        .split('\n')
        .map((a, i) => `${i + 1}`.padEnd(5) + a)
        .join('\n');
      throw `could not compile shader ${this.name}: ${info}, \n${numberedSource}`;
    }
  }

  linkProgram(): void {
    const tStart = performance.now();

    let gl = this.gl;

    this.uniformLocations = {};
    this.attributeLocations = {};
    this.uniforms = {};

    gl.useProgram(null);

    let cached = this.cache.get(`${this.vsSource}, ${this.fsSource}`);
    if (cached) {
      this.program = cached.program;
      this.vs = cached.vs;
      this.fs = cached.fs;
      this.attributeLocations = cached.attributeLocations;
      this.uniformLocations = cached.uniformLocations;
      this.uniformBlocks = cached.uniformBlocks;
      this.uniforms = cached.uniforms;

      return;
    } else {
      this.vs = gl.createShader(gl.VERTEX_SHADER)!;
      this.fs = gl.createShader(gl.FRAGMENT_SHADER)!;
      this.program = gl.createProgram()!;

      for (let name of Object.keys(attributeLocations)) {
        let location = attributeLocations[name].location;
        let glslName = attributeLocations[name].name;
        gl.bindAttribLocation(this.program, location, glslName);
      }

      this.compileShader(this.vs, this.vsSource);
      this.compileShader(this.fs, this.fsSource);

      let program = this.program;

      gl.attachShader(program, this.vs);
      gl.attachShader(program, this.fs);

      gl.linkProgram(program);

      gl.detachShader(program, this.vs);
      gl.detachShader(program, this.fs);

      let success = gl.getProgramParameter(program, gl.LINK_STATUS);
      if (!success) {
        let info = gl.getProgramInfoLog(program);
        throw `could not link program ${this.name}: ${info}`;
      }

      {
        // attribute locations
        let numAttributes = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);

        for (let i = 0; i < numAttributes; i++) {
          let attribute = gl.getActiveAttrib(program, i)!;

          let location = gl.getAttribLocation(program, attribute.name);

          this.attributeLocations[attribute.name] = location;
        }
      }

      {
        // uniform locations
        let numUniforms = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);

        for (let i = 0; i < numUniforms; i++) {
          let uniform = gl.getActiveUniform(program, i)!;

          let location = gl.getUniformLocation(program, uniform.name);

          this.uniformLocations[uniform.name] = location;
          this.uniforms[uniform.name] = {
            location: location,
            value: null,
          };
        }
      }

      // uniform blocks
      {
        let numBlocks = gl.getProgramParameter(program, gl.ACTIVE_UNIFORM_BLOCKS);

        for (let i = 0; i < numBlocks; i++) {
          let blockName = gl.getActiveUniformBlockName(program, i)!;

          let blockIndex = gl.getUniformBlockIndex(program, blockName);

          this.uniformBlockIndices[blockName] = blockIndex;

          gl.uniformBlockBinding(program, blockIndex, blockIndex);
          let dataSize = gl.getActiveUniformBlockParameter(
            program,
            blockIndex,
            gl.UNIFORM_BLOCK_DATA_SIZE,
          );

          let uBuffer = gl.createBuffer()!;
          gl.bindBuffer(gl.UNIFORM_BUFFER, uBuffer);
          gl.bufferData(gl.UNIFORM_BUFFER, dataSize, gl.DYNAMIC_READ);

          gl.bindBufferBase(gl.UNIFORM_BUFFER, blockIndex, uBuffer);

          gl.bindBuffer(gl.UNIFORM_BUFFER, null);

          this.uniformBlocks[blockName] = {
            name: blockName,
            index: blockIndex,
            dataSize: dataSize,
            buffer: uBuffer,
          };
        }
      }

      let cachedEntry = {
        program: this.program,
        vs: this.vs,
        fs: this.fs,
        attributeLocations: this.attributeLocations,
        uniformLocations: this.uniformLocations,
        uniforms: this.uniforms,
        uniformBlocks: this.uniformBlocks,
      };

      this.cache.set(`${this.vsSource}, ${this.fsSource}`, cachedEntry);
    }

    const tEnd = performance.now();
    const duration = tEnd - tStart;

    console.log(`shader compile duration: ${duration.toFixed(3)}`);
  }

  setUniformMatrix4(name: string, value: THREE.Matrix4): void {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    let tmp = new Float32Array(value.elements);
    gl.uniformMatrix4fv(location, false, tmp);
  }

  setUniform1f(name: string, value: number): void {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1f(uniform.location, value);
  }

  setUniformBoolean(name: string, value: boolean): void {
    const gl = this.gl;
    const uniform = this.uniforms[name];

    if (uniform === undefined) {
      return;
    }

    if (uniform.value === value) {
      return;
    }

    uniform.value = value;

    gl.uniform1i(uniform.location, value ? 1 : 0);
  }

  setUniformTexture(name: string, value: number): void {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform1i(location, value);
  }

  setUniform2f(name: string, value: number[]): void {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform2f(location, value[0], value[1]);
  }

  setUniform3f(name: string, value: number[]): void {
    const gl = this.gl;
    const location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform3f(location, value[0], value[1], value[2]);
  }

  setUniform(name: string, value: any): void {
    if (value.constructor === THREE.Matrix4) {
      this.setUniformMatrix4(name, value);
    } else if (typeof value === 'number') {
      this.setUniform1f(name, value);
    } else if (typeof value === 'boolean') {
      this.setUniformBoolean(name, value);
    } else if (value instanceof Array) {
      if (value.length === 2) {
        this.setUniform2f(name, value);
      } else if (value.length === 3) {
        this.setUniform3f(name, value);
      }
    } else {
      console.error('unhandled uniform type: ', name, value);
    }
  }

  setUniform1i(name: string, value: number): void {
    let gl = this.gl;
    let location = this.uniformLocations[name];

    if (location == null) {
      return;
    }

    gl.uniform1i(location, value);
  }
}

class GLTexture {
  gl: WebGL2RenderingContext;
  texture: any;
  id: globalThis.WebGLTexture;
  target: number;
  version: number;

  constructor(gl: WebGL2RenderingContext, texture: any) {
    this.gl = gl;

    this.texture = texture;
    this.id = gl.createTexture()!;

    this.target = gl.TEXTURE_2D;
    this.version = -1;

    this.update(texture);
  }

  update(texture?: any): void {
    if (!this.texture.image) {
      this.version = this.texture.version;

      return;
    }

    let gl = this.gl;
    let tex = this.texture;

    if (this.version === tex.version) {
      return;
    }

    this.target = gl.TEXTURE_2D;

    gl.bindTexture(this.target, this.id);

    let level = 0;
    let internalFormat = paramThreeToGL(gl, tex.format);
    let width = tex.image.width;
    let height = tex.image.height;
    let border = 0;
    let srcFormat = internalFormat;
    let srcType = paramThreeToGL(gl, tex.type);
    let data;

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, tex.flipY);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, tex.premultiplyAlpha);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, tex.unpackAlignment);

    if (tex instanceof THREE.DataTexture) {
      data = tex.image.data;

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, tex.magFilter));
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, tex.minFilter));

      gl.texImage2D(
        this.target,
        level,
        internalFormat,
        width,
        height,
        border,
        srcFormat,
        srcType,
        data,
      );
    } else if (tex instanceof THREE.CanvasTexture || tex instanceof THREE.Texture) {
      data = tex.image;

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, paramThreeToGL(gl, tex.wrapS));
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, paramThreeToGL(gl, tex.wrapT));

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, tex.magFilter));
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, tex.minFilter));

      gl.texImage2D(this.target, level, internalFormat, internalFormat, srcType, data);

      if (tex instanceof THREE.Texture) {
        gl.generateMipmap(gl.TEXTURE_2D);
      }
    }

    gl.bindTexture(this.target, null);

    this.version = tex.version;
  }
}

class WebGLBuffer {
  numElements: number;
  vao: WebGLVertexArrayObject | null;
  vbos: Map<string, any>;

  constructor() {
    this.numElements = 0;
    this.vao = null;
    this.vbos = new Map();
  }
}

export class Renderer {
  threeRenderer: THREE.WebGLRenderer;
  gl: WebGL2RenderingContext;
  buffers: Map<any, WebGLBuffer>;
  shaders: Map<any, Shader>;
  textures: Map<any, GLTexture>;
  glTypeMapping: Map<any, number>;
  toggle: number;

  constructor(threeRenderer: THREE.WebGLRenderer) {
    this.threeRenderer = threeRenderer;
    this.gl = this.threeRenderer.getContext() as WebGL2RenderingContext;

    this.buffers = new Map();
    this.shaders = new Map();
    this.textures = new Map();

    this.glTypeMapping = new Map();
    this.glTypeMapping.set(Float32Array, this.gl.FLOAT);
    this.glTypeMapping.set(Uint8Array, this.gl.UNSIGNED_BYTE);
    this.glTypeMapping.set(Uint16Array, this.gl.UNSIGNED_SHORT);

    this.toggle = 0;
  }

  deleteBuffer(geometry: any): void {
    let gl = this.gl;
    let webglBuffer = this.buffers.get(geometry);
    if (webglBuffer != null) {
      for (let attributeName in geometry.attributes) {
        gl.deleteBuffer(webglBuffer.vbos.get(attributeName).handle);
      }
      this.buffers.delete(geometry);
    }
  }

  createBuffer(geometry: any): WebGLBuffer {
    let gl = this.gl;
    let webglBuffer = new WebGLBuffer();
    webglBuffer.vao = gl.createVertexArray();
    webglBuffer.numElements = geometry.attributes.position.count;

    gl.bindVertexArray(webglBuffer.vao);

    for (let attributeName in geometry.attributes) {
      let bufferAttribute = geometry.attributes[attributeName];

      let vbo = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW);

      let normalized = bufferAttribute.normalized;
      let type = this.glTypeMapping.get(bufferAttribute.array.constructor);

      if (attributeLocations[attributeName] !== undefined) {
        let attributeLocation = attributeLocations[attributeName].location;

        gl.vertexAttribPointer(
          attributeLocation,
          bufferAttribute.itemSize,
          type!,
          normalized,
          0,
          0,
        );
        gl.enableVertexAttribArray(attributeLocation);
      }

      webglBuffer.vbos.set(attributeName, {
        handle: vbo,
        name: attributeName,
        count: bufferAttribute.count,
        itemSize: bufferAttribute.itemSize,
        type: geometry.attributes.position.array.constructor,
        version: 0,
      });
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);

    let disposeHandler = (event: any) => {
      this.deleteBuffer(geometry);
      geometry.removeEventListener('dispose', disposeHandler);
    };
    geometry.addEventListener('dispose', disposeHandler);

    return webglBuffer;
  }

  updateBuffer(geometry: any): void {
    let gl = this.gl;

    let webglBuffer = this.buffers.get(geometry)!;

    gl.bindVertexArray(webglBuffer.vao);

    for (let attributeName in geometry.attributes) {
      let bufferAttribute = geometry.attributes[attributeName];

      let normalized = bufferAttribute.normalized;
      let type = this.glTypeMapping.get(bufferAttribute.array.constructor);

      let vbo: any = null;
      if (!webglBuffer.vbos.has(attributeName)) {
        vbo = gl.createBuffer();

        webglBuffer.vbos.set(attributeName, {
          handle: vbo,
          name: attributeName,
          count: bufferAttribute.count,
          itemSize: bufferAttribute.itemSize,
          type: geometry.attributes.position.array.constructor,
          version: bufferAttribute.version,
        });
      } else {
        vbo = webglBuffer.vbos.get(attributeName).handle;
        webglBuffer.vbos.get(attributeName).version = bufferAttribute.version;
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW);

      if (attributeLocations[attributeName] !== undefined) {
        let attributeLocation = attributeLocations[attributeName].location;

        gl.vertexAttribPointer(
          attributeLocation,
          bufferAttribute.itemSize,
          type!,
          normalized,
          0,
          0,
        );
        gl.enableVertexAttribArray(attributeLocation);
      }
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  traverse(scene: THREE.Scene): { octrees: PointCloudTree[] } {
    let octrees: PointCloudTree[] = [];

    let stack: any[] = [scene];
    while (stack.length > 0) {
      let node = stack.pop();

      if (node instanceof PointCloudTree) {
        octrees.push(node);
        continue;
      }

      let visibleChildren = node.children.filter((c: any) => c.visible);
      stack.push(...visibleChildren);
    }

    let result = {
      octrees: octrees,
    };

    return result;
  }

  renderNodes(
    octree: any,
    nodes: any[],
    visibilityTextureData: any,
    camera: any,
    target: any,
    shader: Shader,
    params: any,
  ): void {
    let gl = this.gl;

    let material = params.material ? params.material : octree.material;
    let shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps;
    let view = camera.matrixWorldInverse;

    if (params.viewOverride) {
      view = params.viewOverride;
    }

    let worldView = new THREE.Matrix4();

    let mat4holder = new Float32Array(16);

    let i = 0;
    for (let node of nodes) {
      if (debug && debug.allowedNodes !== undefined) {
        if (!debug.allowedNodes.includes(node.name)) {
          continue;
        }
      }

      let world = node.sceneNode.matrixWorld;
      worldView.multiplyMatrices(view, world);

      if (visibilityTextureData) {
        let vnStart = visibilityTextureData.offsets.get(node);
        shader.setUniform1f('uVNStart', vnStart);
      }

      let level = node.getLevel();

      if (node.debug) {
        shader.setUniform('uDebug', true);
      } else {
        shader.setUniform('uDebug', false);
      }

      const lModel = shader.uniformLocations['modelMatrix'];
      if (lModel) {
        mat4holder.set(world.elements);
        gl.uniformMatrix4fv(lModel, false, mat4holder);
      }

      const lModelView = shader.uniformLocations['modelViewMatrix'];
      for (let j = 0; j < 16; j++) {
        mat4holder[j] = worldView.elements[j];
      }
      gl.uniformMatrix4fv(lModelView, false, mat4holder);

      {
        // Clip Polygons
        if (material.clipPolygons && material.clipPolygons.length > 0) {
          let clipPolygonVCount: number[] = [];
          let worldViewProjMatrices: THREE.Matrix4[] = [];

          for (let clipPolygon of material.clipPolygons) {
            let view = clipPolygon.viewMatrix;
            let proj = clipPolygon.projMatrix;

            let worldViewProj = proj.clone().multiply(view).multiply(world);

            clipPolygonVCount.push(clipPolygon.markers.length);
            worldViewProjMatrices.push(worldViewProj);
          }

          let flattenedMatrices = ([] as number[]).concat(
            ...worldViewProjMatrices.map(m => m.elements as unknown as number[]),
          );

          let flattenedVertices = new Array(8 * 3 * material.clipPolygons.length);
          for (let i = 0; i < material.clipPolygons.length; i++) {
            let clipPolygon = material.clipPolygons[i];
            for (let j = 0; j < clipPolygon.markers.length; j++) {
              flattenedVertices[i * 24 + (j * 3 + 0)] = clipPolygon.markers[j].position.x;
              flattenedVertices[i * 24 + (j * 3 + 1)] = clipPolygon.markers[j].position.y;
              flattenedVertices[i * 24 + (j * 3 + 2)] = clipPolygon.markers[j].position.z;
            }
          }

          const lClipPolygonVCount = shader.uniformLocations['uClipPolygonVCount[0]'];
          gl.uniform1iv(lClipPolygonVCount, clipPolygonVCount);

          const lClipPolygonVP = shader.uniformLocations['uClipPolygonWVP[0]'];
          gl.uniformMatrix4fv(lClipPolygonVP, false, flattenedMatrices);

          const lClipPolygons = shader.uniformLocations['uClipPolygonVertices[0]'];
          gl.uniform3fv(lClipPolygons, flattenedVertices);
        }
      }

      shader.setUniform1f('uLevel', level);
      shader.setUniform1f('uNodeSpacing', node.geometryNode.estimatedSpacing);

      shader.setUniform1f('uPCIndex', i);

      if (shadowMaps.length > 0) {
        const lShadowMap = shader.uniformLocations['uShadowMap[0]'];

        shader.setUniform3f('uShadowColor', material.uniforms.uShadowColor.value);

        let bindingStart = 5;
        let bindingPoints = new Array(shadowMaps.length)
          .fill(bindingStart)
          .map((a: number, i: number) => a + i);
        gl.uniform1iv(lShadowMap, bindingPoints);

        for (let i = 0; i < shadowMaps.length; i++) {
          let shadowMap = shadowMaps[i];
          let bindingPoint = bindingPoints[i];
          let glTexture = (this.threeRenderer as any).properties.get(
            shadowMap.target.texture,
          ).__webglTexture;

          gl.activeTexture((gl as any)[`TEXTURE${bindingPoint}`]);
          gl.bindTexture(gl.TEXTURE_2D, glTexture);
        }

        {
          let worldViewMatrices = shadowMaps
            .map((sm: any) => sm.camera.matrixWorldInverse)
            .map((view: any) => new THREE.Matrix4().multiplyMatrices(view, world));

          let flattenedMatrices = ([] as number[]).concat(
            ...worldViewMatrices.map((c: any) => c.elements as unknown as number[]),
          );
          const lWorldView = shader.uniformLocations['uShadowWorldView[0]'];
          gl.uniformMatrix4fv(lWorldView, false, flattenedMatrices);
        }

        {
          let flattenedMatrices = ([] as number[]).concat(
            ...shadowMaps.map(
              (sm: any) => sm.camera.projectionMatrix.elements as unknown as number[],
            ),
          );
          const lProj = shader.uniformLocations['uShadowProj[0]'];
          gl.uniformMatrix4fv(lProj, false, flattenedMatrices);
        }
      }

      const geometry = node.geometryNode.geometry;

      if (!geometry) console.log('Missing geometry', node);
      if (geometry.attributes['gps-time']) {
        const bufferAttribute = geometry.attributes['gps-time'];
        const attGPS = octree.getAttribute('gps-time');

        let initialRange = attGPS.initialRange;
        let initialRangeSize = initialRange[1] - initialRange[0];

        let globalRange = attGPS.range;
        let globalRangeSize = globalRange[1] - globalRange[0];

        let scale = initialRangeSize / globalRangeSize;
        let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize;

        scale = Number.isNaN(scale) ? 1 : scale;
        offset = Number.isNaN(offset) ? 0 : offset;

        shader.setUniform1f('uGpsScale', scale);
        shader.setUniform1f('uGpsOffset', offset);

        let uFilterGPSTimeClipRange = material.uniforms.uFilterGPSTimeClipRange.value;

        let normalizedClipRange = [
          (uFilterGPSTimeClipRange[0] - globalRange[0]) / globalRangeSize,
          (uFilterGPSTimeClipRange[1] - globalRange[0]) / globalRangeSize,
        ];

        shader.setUniform2f('uFilterGPSTimeClipRange', normalizedClipRange);
      }

      {
        let uFilterReturnNumberRange = material.uniforms.uFilterReturnNumberRange.value;
        let uFilterNumberOfReturnsRange = material.uniforms.uFilterNumberOfReturnsRange.value;
        let uFilterPointSourceIDClipRange = material.uniforms.uFilterPointSourceIDClipRange.value;

        shader.setUniform2f('uFilterReturnNumberRange', uFilterReturnNumberRange);
        shader.setUniform2f('uFilterNumberOfReturnsRange', uFilterNumberOfReturnsRange);
        shader.setUniform2f('uFilterPointSourceIDClipRange', uFilterPointSourceIDClipRange);
      }

      let webglBuffer: WebGLBuffer | null = null;
      if (!this.buffers.has(geometry)) {
        webglBuffer = this.createBuffer(geometry);
        this.buffers.set(geometry, webglBuffer);
      } else {
        webglBuffer = this.buffers.get(geometry)!;
        for (let attributeName in geometry.attributes) {
          let attribute = geometry.attributes[attributeName];

          if (attribute.version > webglBuffer.vbos.get(attributeName).version) {
            this.updateBuffer(geometry);
          }
        }
      }

      gl.bindVertexArray(webglBuffer.vao);

      let isExtraAttribute =
        attributeLocations[material.activeAttributeName] === undefined &&
        Object.keys(geometry.attributes).includes(material.activeAttributeName);

      if (isExtraAttribute) {
        const extraAttributeLocation = attributeLocations['aExtra'].location;

        for (const attributeName in geometry.attributes) {
          const vbo = webglBuffer.vbos.get(attributeName);

          gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
          gl.disableVertexAttribArray(extraAttributeLocation);
        }

        const attName = material.activeAttributeName;
        const bufferAttribute = geometry.attributes[attName];
        const vbo = webglBuffer.vbos.get(attName);

        if (bufferAttribute !== undefined && vbo !== undefined) {
          let type = this.glTypeMapping.get(bufferAttribute.array.constructor)!;
          let normalized = bufferAttribute.normalized;

          gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
          gl.vertexAttribPointer(
            extraAttributeLocation,
            bufferAttribute.itemSize,
            type,
            normalized,
            0,
            0,
          );
          gl.enableVertexAttribArray(extraAttributeLocation);
        }

        {
          const attExtra = octree.pcoGeometry.pointAttributes.attributes.find(
            (a: any) => a.name === attName,
          );

          let range = material.getRange(attName);
          if (!range) {
            range = attExtra.range;
          }

          if (!range) {
            range = [0, 1];
          }

          let initialRange = attExtra.initialRange;
          let initialRangeSize = initialRange[1] - initialRange[0];

          let globalRange = range;
          let globalRangeSize = globalRange[1] - globalRange[0];

          let scale = initialRangeSize / globalRangeSize;
          let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize;

          scale = Number.isNaN(scale) ? 1 : scale;
          offset = Number.isNaN(offset) ? 0 : offset;

          shader.setUniform1f('uExtraScale', scale);
          shader.setUniform1f('uExtraOffset', offset);
        }
      } else {
        for (const attributeName in geometry.attributes) {
          const bufferAttribute = geometry.attributes[attributeName];
          const vbo = webglBuffer.vbos.get(attributeName);

          if (attributeLocations[attributeName] !== undefined) {
            const attrLocation = attributeLocations[attributeName].location;

            let type = this.glTypeMapping.get(bufferAttribute.array.constructor)!;
            let normalized = bufferAttribute.normalized;

            gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle);
            gl.vertexAttribPointer(attrLocation, bufferAttribute.itemSize, type, normalized, 0, 0);
            gl.enableVertexAttribArray(attrLocation);
          }
        }
      }

      let numPoints = webglBuffer.numElements;
      gl.drawArrays(gl.POINTS, 0, numPoints);

      i++;
    }

    gl.bindVertexArray(null);
  }

  renderOctree(octree: any, nodes: any[], camera: any, target: any, params: any = {}): void {
    let gl = this.gl;

    let material = params.material ? params.material : octree.material;
    let shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps;
    let view = camera.matrixWorldInverse;
    let viewInv = camera.matrixWorld;

    if (params.viewOverride) {
      view = params.viewOverride;
      viewInv = view.clone().invert();
    }

    let proj = camera.projectionMatrix;
    let projInv = proj.clone().invert();

    let shader: Shader | null = null;
    let visibilityTextureData: any = null;

    let currentTextureBindingPoint = 0;

    if (material.pointSizeType >= 0) {
      if (
        material.pointSizeType === PointSizeType.ADAPTIVE ||
        material.activeAttributeName === 'level of detail'
      ) {
        let vnNodes = params.vnTextureNodes != null ? params.vnTextureNodes : nodes;
        visibilityTextureData = octree.computeVisibilityTextureData(vnNodes, camera);

        const vnt = material.visibleNodesTexture;
        const data = vnt.image.data;
        data.set(visibilityTextureData.data);
        vnt.needsUpdate = true;
      }
    }

    {
      // UPDATE SHADER AND TEXTURES
      if (!this.shaders.has(material)) {
        let [vs, fs] = [material.vertexShader, material.fragmentShader];
        let s = new Shader(gl, 'pointcloud', vs, fs);

        this.shaders.set(material, s);
      }

      shader = this.shaders.get(material)!;

      {
        let [vs, fs] = [material.vertexShader, material.fragmentShader];

        let numSnapshots = material.snapEnabled ? material.numSnapshots : 0;
        let numClipBoxes =
          material.clipBoxes && material.clipBoxes.length ? material.clipBoxes.length : 0;
        let numClipSpheres =
          params.clipSpheres && params.clipSpheres.length ? params.clipSpheres.length : 0;
        let numClipPolygons =
          material.clipPolygons && material.clipPolygons.length ? material.clipPolygons.length : 0;

        let defines = [
          `#define num_shadowmaps ${shadowMaps.length}`,
          `#define num_snapshots ${numSnapshots}`,
          `#define num_clipboxes ${numClipBoxes}`,
          `#define num_clipspheres ${numClipSpheres}`,
          `#define num_clippolygons ${numClipPolygons}`,
        ];

        if (octree.pcoGeometry.root.isLoaded()) {
          let attributes = octree.pcoGeometry.root.geometry.attributes;

          if (attributes['gps-time']) {
            defines.push('#define clip_gps_enabled');
          }

          if (attributes['return number']) {
            defines.push('#define clip_return_number_enabled');
          }

          if (attributes['number of returns']) {
            defines.push('#define clip_number_of_returns_enabled');
          }

          if (attributes['source id'] || attributes['point source id']) {
            defines.push('#define clip_point_source_id_enabled');
          }
        }

        let definesString = defines.join('\n');

        let vsVersionIndex = vs.indexOf('#version ');
        let fsVersionIndex = fs.indexOf('#version ');

        if (vsVersionIndex >= 0) {
          vs = vs.replace(/(#version .*)/, `$1\n${definesString}`);
        } else {
          vs = `${definesString}\n${vs}`;
        }

        if (fsVersionIndex >= 0) {
          fs = fs.replace(/(#version .*)/, `$1\n${definesString}`);
        } else {
          fs = `${definesString}\n${fs}`;
        }

        shader.update(vs, fs);

        material.needsUpdate = false;
      }

      for (let uniformName of Object.keys(material.uniforms)) {
        let uniform = material.uniforms[uniformName];

        if (uniform.type == 't') {
          let texture = uniform.value;

          if (!texture) {
            continue;
          }

          if (!this.textures.has(texture)) {
            let webglTexture = new GLTexture(gl, texture);

            this.textures.set(texture, webglTexture);
          }

          let webGLTexture = this.textures.get(texture)!;
          webGLTexture.update();
        }
      }
    }

    gl.useProgram(shader!.program);

    let transparent = false;
    if (params.transparent !== undefined) {
      transparent = params.transparent && material.opacity < 1;
    } else {
      transparent = material.opacity < 1;
    }

    if (transparent) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      gl.depthMask(false);
      gl.disable(gl.DEPTH_TEST);
    } else {
      gl.disable(gl.BLEND);
      gl.depthMask(true);
      gl.enable(gl.DEPTH_TEST);
    }

    if (params.blendFunc !== undefined) {
      gl.enable(gl.BLEND);
      gl.blendFunc(params.blendFunc[0], params.blendFunc[1]);
    }

    if (params.depthTest !== undefined) {
      if (params.depthTest === true) {
        gl.enable(gl.DEPTH_TEST);
      } else {
        gl.disable(gl.DEPTH_TEST);
      }
    }

    if (params.depthWrite !== undefined) {
      if (params.depthWrite === true) {
        gl.depthMask(true);
      } else {
        gl.depthMask(false);
      }
    }

    {
      // UPDATE UNIFORMS
      shader!.setUniformMatrix4('projectionMatrix', proj);
      shader!.setUniformMatrix4('viewMatrix', view);
      shader!.setUniformMatrix4('uViewInv', viewInv);
      shader!.setUniformMatrix4('uProjInv', projInv);

      let screenWidth = target ? target.width : material.screenWidth;
      let screenHeight = target ? target.height : material.screenHeight;

      shader!.setUniform1f('uScreenWidth', screenWidth);
      shader!.setUniform1f('uScreenHeight', screenHeight);
      shader!.setUniform1f('fov', (Math.PI * camera.fov) / 180);
      shader!.setUniform1f('near', camera.near);
      shader!.setUniform1f('far', camera.far);

      if (camera instanceof THREE.OrthographicCamera) {
        shader!.setUniform('uUseOrthographicCamera', true);
        shader!.setUniform('uOrthoWidth', camera.right - camera.left);
        shader!.setUniform('uOrthoHeight', camera.top - camera.bottom);
      } else {
        shader!.setUniform('uUseOrthographicCamera', false);
      }

      if (material.clipBoxes.length + material.clipPolygons.length === 0) {
        shader!.setUniform1i('clipTask', ClipTask.NONE);
      } else {
        shader!.setUniform1i('clipTask', material.clipTask);
      }

      shader!.setUniform1i('clipMethod', material.clipMethod);

      if (material.clipBoxes && material.clipBoxes.length > 0) {
        const lClipBoxes = shader!.uniformLocations['clipBoxes[0]'];
        gl.uniformMatrix4fv(lClipBoxes, false, material.uniforms.clipBoxes.value);
      }

      // TODO CLIPSPHERES
      if (params.clipSpheres && params.clipSpheres.length > 0) {
        let clipSpheres = params.clipSpheres;

        let matrices: THREE.Matrix4[] = [];
        for (let clipSphere of clipSpheres) {
          let clipToWorld = clipSphere.matrixWorld;
          let viewToWorld = camera.matrixWorld;
          let worldToClip = clipToWorld.clone().invert();

          let viewToClip = new THREE.Matrix4().multiplyMatrices(worldToClip, viewToWorld);

          matrices.push(viewToClip);
        }

        let flattenedMatrices = ([] as number[]).concat(
          ...matrices.map(matrix => matrix.elements as unknown as number[]),
        );

        const lClipSpheres = shader!.uniformLocations['uClipSpheres[0]'];
        gl.uniformMatrix4fv(lClipSpheres, false, flattenedMatrices);
      }

      shader!.setUniform1f('size', material.size);
      shader!.setUniform1f('maxSize', material.uniforms.maxSize.value);
      shader!.setUniform1f('minSize', material.uniforms.minSize.value);

      shader!.setUniform1f('uOctreeSpacing', material.spacing);
      shader!.setUniform('uOctreeSize', material.uniforms.octreeSize.value);

      shader!.setUniform3f('uColor', material.color.toArray());
      shader!.setUniform1f('uOpacity', material.opacity);

      shader!.setUniform2f('elevationRange', material.elevationRange);
      shader!.setUniform2f('intensityRange', material.intensityRange);

      shader!.setUniform3f('uIntensity_gbc', [
        material.intensityGamma,
        material.intensityBrightness,
        material.intensityContrast,
      ]);

      shader!.setUniform3f('uRGB_gbc', [
        material.rgbGamma,
        material.rgbBrightness,
        material.rgbContrast,
      ]);

      shader!.setUniform1f('uTransition', material.transition);
      shader!.setUniform1f('wRGB', material.weightRGB);
      shader!.setUniform1f('wIntensity', material.weightIntensity);
      shader!.setUniform1f('wElevation', material.weightElevation);
      shader!.setUniform1f('wClassification', material.weightClassification);
      shader!.setUniform1f('wReturnNumber', material.weightReturnNumber);
      shader!.setUniform1f('wSourceID', material.weightSourceID);

      shader!.setUniform('backfaceCulling', material.uniforms.backfaceCulling.value);

      let vnWebGLTexture = this.textures.get(material.visibleNodesTexture);
      if (vnWebGLTexture) {
        shader!.setUniform1i('visibleNodesTexture', currentTextureBindingPoint);
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
        gl.bindTexture(vnWebGLTexture.target, vnWebGLTexture.id);
        currentTextureBindingPoint++;
      }

      let gradientTexture = this.textures.get(material.gradientTexture)!;
      shader!.setUniform1i('gradient', currentTextureBindingPoint);
      gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
      gl.bindTexture(gradientTexture.target, gradientTexture.id);

      const repeat = material.elevationGradientRepeat;
      if (repeat === ElevationGradientRepeat.REPEAT) {
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.REPEAT);
      } else if (repeat === ElevationGradientRepeat.MIRRORED_REPEAT) {
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
      } else {
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      }
      currentTextureBindingPoint++;

      let classificationTexture = this.textures.get(material.classificationTexture)!;
      shader!.setUniform1i('classificationLUT', currentTextureBindingPoint);
      gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint);
      gl.bindTexture(classificationTexture.target, classificationTexture.id);
      currentTextureBindingPoint++;

      if (material.snapEnabled === true) {
        {
          const lSnapshot = shader!.uniformLocations['uSnapshot[0]'];
          const lSnapshotDepth = shader!.uniformLocations['uSnapshotDepth[0]'];

          let bindingStart = currentTextureBindingPoint;
          let lSnapshotBindingPoints = new Array(5)
            .fill(bindingStart)
            .map((a: number, i: number) => a + i);
          let lSnapshotDepthBindingPoints = new Array(5)
            .fill(1 + Math.max(...lSnapshotBindingPoints))
            .map((a: number, i: number) => a + i);
          currentTextureBindingPoint = 1 + Math.max(...lSnapshotDepthBindingPoints);

          gl.uniform1iv(lSnapshot, lSnapshotBindingPoints);
          gl.uniform1iv(lSnapshotDepth, lSnapshotDepthBindingPoints);

          for (let i = 0; i < 5; i++) {
            let texture = material.uniforms[`uSnapshot`].value[i];
            let textureDepth = material.uniforms[`uSnapshotDepth`].value[i];

            if (!texture) {
              break;
            }

            let snapTexture = (this.threeRenderer as any).properties.get(texture).__webglTexture;
            let snapTextureDepth = (this.threeRenderer as any).properties.get(
              textureDepth,
            ).__webglTexture;

            let bindingPoint = lSnapshotBindingPoints[i];
            let depthBindingPoint = lSnapshotDepthBindingPoints[i];

            gl.activeTexture((gl as any)[`TEXTURE${bindingPoint}`]);
            gl.bindTexture(gl.TEXTURE_2D, snapTexture);

            gl.activeTexture((gl as any)[`TEXTURE${depthBindingPoint}`]);
            gl.bindTexture(gl.TEXTURE_2D, snapTextureDepth);
          }
        }

        {
          let flattenedMatrices = ([] as number[]).concat(
            ...material.uniforms.uSnapView.value.map((c: any) => c.elements as unknown as number[]),
          );
          const lSnapView = shader!.uniformLocations['uSnapView[0]'];
          gl.uniformMatrix4fv(lSnapView, false, flattenedMatrices);
        }
        {
          let flattenedMatrices = ([] as number[]).concat(
            ...material.uniforms.uSnapProj.value.map((c: any) => c.elements as unknown as number[]),
          );
          const lSnapProj = shader!.uniformLocations['uSnapProj[0]'];
          gl.uniformMatrix4fv(lSnapProj, false, flattenedMatrices);
        }
        {
          let flattenedMatrices = ([] as number[]).concat(
            ...material.uniforms.uSnapProjInv.value.map(
              (c: any) => c.elements as unknown as number[],
            ),
          );
          const lSnapProjInv = shader!.uniformLocations['uSnapProjInv[0]'];
          gl.uniformMatrix4fv(lSnapProjInv, false, flattenedMatrices);
        }
        {
          let flattenedMatrices = ([] as number[]).concat(
            ...material.uniforms.uSnapViewInv.value.map(
              (c: any) => c.elements as unknown as number[],
            ),
          );
          const lSnapViewInv = shader!.uniformLocations['uSnapViewInv[0]'];
          gl.uniformMatrix4fv(lSnapViewInv, false, flattenedMatrices);
        }
      }
    }

    this.renderNodes(octree, nodes, visibilityTextureData, camera, target, shader!, params);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
  }

  render(scene: THREE.Scene, camera: any, target: any = null, params: any = {}): void {
    const gl = this.gl;

    // PREPARE
    if (target != null) {
      this.threeRenderer.setRenderTarget(target);
    }

    const traversalResult = this.traverse(scene);

    // RENDER
    for (const octree of traversalResult.octrees) {
      let nodes = (octree as any).visibleNodes;
      this.renderOctree(octree, nodes, camera, target, params);
    }

    // CLEANUP
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);

    this.threeRenderer.resetState();
  }
}
